import { z } from 'zod';

/**
 * Retail Media E1 (Briefing Teil E, Abschnitt 2): Produktgruppen.
 *
 * Eine Produktgruppe ist eine gespeicherte, wiederverwendbare Auswahl beworbener Produkte eines Werbekontos:
 * alle         alle Produkte des Werbekontos (Händler: eigene, Marke: alle Produkte ihrer Marke im Marktplatz)
 * statisch     feste Auswahl aus der Produktliste
 * regel        dynamisch nach Regel (Format 2.4) – wird bei Katalogänderung und alle 15 Minuten neu ausgewertet
 * liste        EAN-/SKU-Liste (einfügen oder CSV/Excel), Ergebnis als feste Einträge
 * In jeder Gruppe lassen sich Produkte und eine Ausschluss-Regel ausnehmen.
 *
 * Die Regel wird an zwei Stellen aus derselben Definition ausgewertet: in SQL für Vorschau und Zähler (apps/api,
 * lib/produktgruppen.ts → regelSql) und im Speicher für die Auktion (regelPasst hier). Beide lesen dieselben Fakten
 * je Produkt (ProduktFakten, berechnet von faktenAbfrage) – ein Test stellt sicher, dass beide gleich entscheiden.
 */

export const PRODUKTGRUPPE_ARTEN = {
  alle: 'Alle meine Produkte',
  statisch: 'Auswahl',
  regel: 'Regel (dynamisch)',
  liste: 'EAN-/SKU-Liste',
} as const;
export type ProduktgruppeArt = keyof typeof PRODUKTGRUPPE_ARTEN;
export const PRODUKTGRUPPE_ART_LISTE = ['alle', 'statisch', 'regel', 'liste'] as const satisfies readonly ProduktgruppeArt[];

export const PRODUKTGRUPPE_GRENZEN = {
  gruppenJeKonto: 500,
  /** feste Einträge je Gruppe (Auswahl, Liste) */
  eintraegeJeGruppe: 50_000,
  eintraegeJeAufruf: 1_000,
  /** EAN-/SKU-Liste: Zeilen und Bytes je Import */
  importZeilen: 50_000,
  importBytes: 8 * 1024 * 1024,
  bedingungen: 30,
  tiefe: 3,
  textWerte: 100,
  vorschau: 50,
  gruppenJeAnzeigengruppe: 10,
  /** Schnellkampagne: Produkte je Aufruf */
  schnellProdukte: 200,
} as const;

/** Bestseller = Top 20 % nach Umsatz der letzten 30 Tage (unter den Produkten mit Umsatz) */
export const BESTSELLER_PROZENT = 20;
/** Lagerdreher = Bestand ab 10 Stück und Absatz der letzten 30 Tage höchstens 10 % des Bestands */
export const LAGERDREHER = { bestandAb: 10, absatzAnteil: 0.1 } as const;
/** Dynamische Gruppen spätestens nach dieser Zeit neu auswerten */
export const PRODUKTGRUPPE_NEU_AUSWERTEN_MINUTEN = 15;

/* ------------------------------------------------------------------ Regelformat (2.4) */

export const REGEL_TEXT_FELDER = ['marke', 'kategorie', 'merkmal', 'titel'] as const;
export const REGEL_ZAHL_FELDER = ['preis', 'bestand', 'bewertung', 'rabatt_prozent', 'neu_seit_tagen'] as const;
export const REGEL_JA_FELDER = ['buybox_gewinner', 'bestseller', 'lagerdreher'] as const;
export type RegelTextFeld = (typeof REGEL_TEXT_FELDER)[number];
export type RegelZahlFeld = (typeof REGEL_ZAHL_FELDER)[number];
export type RegelJaFeld = (typeof REGEL_JA_FELDER)[number];
export type RegelFeld = RegelTextFeld | RegelZahlFeld | RegelJaFeld;

export const REGEL_TEXT_OPS = ['ist', 'ist_nicht', 'enthaelt', 'in'] as const;
export const REGEL_ZAHL_OPS = ['>=', '<=', '=', 'zwischen'] as const;

export type Bedingung =
  | { feld: RegelTextFeld; op: (typeof REGEL_TEXT_OPS)[number]; wert: string | string[]; schluessel?: string }
  | { feld: RegelZahlFeld; op: (typeof REGEL_ZAHL_OPS)[number]; wert: number | [number, number] }
  | { feld: RegelJaFeld; op: 'ist'; wert: boolean };
export type Regel = { und?: (Bedingung | Regel)[]; oder?: (Bedingung | Regel)[] };

/** Bezeichnungen für den Regel-Baukasten */
export const REGEL_FELDER: Record<RegelFeld, { name: string; art: 'text' | 'zahl' | 'ja'; einheit?: string; hilfe?: string }> = {
  marke: { name: 'Marke', art: 'text' },
  kategorie: { name: 'Kategorie', art: 'text', hilfe: 'inklusive Unterkategorien' },
  merkmal: { name: 'Merkmal', art: 'text', hilfe: 'Produktfeld, z. B. custom.material' },
  titel: { name: 'Titel', art: 'text' },
  preis: { name: 'Preis', art: 'zahl', einheit: '€' },
  bestand: { name: 'Bestand', art: 'zahl', einheit: 'Stück' },
  bewertung: { name: 'Bewertung', art: 'zahl', hilfe: 'Bewertung des Händlers, 0–5' },
  rabatt_prozent: { name: 'Rabatt', art: 'zahl', einheit: '%' },
  neu_seit_tagen: { name: 'Im Katalog seit', art: 'zahl', einheit: 'Tagen' },
  buybox_gewinner: { name: 'Hält die Buy Box', art: 'ja' },
  bestseller: { name: `Bestseller (Top ${BESTSELLER_PROZENT} % Umsatz, 30 Tage)`, art: 'ja' },
  lagerdreher: { name: 'Lagerdreher (Bestand hoch, Absatz niedrig)', art: 'ja' },
};
export const REGEL_OP_NAMEN: Record<string, string> = {
  ist: 'ist', ist_nicht: 'ist nicht', enthaelt: 'enthält', in: 'ist eines von', '>=': 'mindestens', '<=': 'höchstens', '=': 'gleich', zwischen: 'zwischen',
};

const TextWert = z.string().trim().min(1, 'Bitte Wert angeben').max(200);
const ZahlWert = z.number().finite().min(-1_000_000).max(100_000_000);

const BedingungSchema: z.ZodType<Bedingung> = z.union([
  z.object({
    feld: z.enum(REGEL_TEXT_FELDER),
    op: z.enum(REGEL_TEXT_OPS),
    wert: z.union([TextWert, z.array(TextWert).min(1).max(PRODUKTGRUPPE_GRENZEN.textWerte)]),
    schluessel: z.string().trim().min(1).max(120).optional(),
  }).strict().refine((b) => b.feld !== 'merkmal' || !!b.schluessel, { message: 'Bitte das Merkmal angeben', path: ['schluessel'] }),
  z.object({
    feld: z.enum(REGEL_ZAHL_FELDER),
    op: z.enum(REGEL_ZAHL_OPS),
    wert: z.union([ZahlWert, z.tuple([ZahlWert, ZahlWert])]),
  }).strict().refine((b) => (b.op === 'zwischen') === Array.isArray(b.wert), { message: 'Bei „zwischen“ bitte zwei Werte angeben', path: ['wert'] }),
  z.object({ feld: z.enum(REGEL_JA_FELDER), op: z.literal('ist'), wert: z.boolean() }).strict(),
]);

const RegelRoh: z.ZodType<Regel> = z.lazy(() => z.object({
  und: z.array(z.union([BedingungSchema, RegelRoh])).max(PRODUKTGRUPPE_GRENZEN.bedingungen).optional(),
  oder: z.array(z.union([BedingungSchema, RegelRoh])).max(PRODUKTGRUPPE_GRENZEN.bedingungen).optional(),
}).strict());

export const istBedingung = (x: Bedingung | Regel): x is Bedingung => 'feld' in x;

/** Anzahl der Bedingungen und Schachtelungstiefe einer Regel */
export function regelUmfang(r: Regel, tiefe = 1): { bedingungen: number; tiefe: number } {
  let bedingungen = 0;
  let max = tiefe;
  for (const t of [...(r.und ?? []), ...(r.oder ?? [])]) {
    if (istBedingung(t)) bedingungen += 1;
    else {
      const u = regelUmfang(t, tiefe + 1);
      bedingungen += u.bedingungen;
      max = Math.max(max, u.tiefe);
    }
  }
  return { bedingungen, tiefe: max };
}

export const RegelSchema = RegelRoh.superRefine((r, ctx) => {
  const u = regelUmfang(r);
  if (u.bedingungen > PRODUKTGRUPPE_GRENZEN.bedingungen) ctx.addIssue({ code: 'custom', message: `Höchstens ${PRODUKTGRUPPE_GRENZEN.bedingungen} Bedingungen` });
  if (u.tiefe > PRODUKTGRUPPE_GRENZEN.tiefe) ctx.addIssue({ code: 'custom', message: `Höchstens ${PRODUKTGRUPPE_GRENZEN.tiefe} Ebenen` });
});

/* ------------------------------------------------------------------ Fakten und Auswertung im Speicher */

/** Was die Regel und die Bewerbbarkeit je Produkt lesen – in SQL und im Speicher dieselben Werte */
export interface ProduktFakten {
  id: string;
  titel: string;
  marke: string;
  kategorie: string;
  felder: Record<string, string>;
  status: string;
  /** günstigste Variante in € */
  preis: number | null;
  /** Summe der Varianten; null = Bestand wird nicht geführt (gilt als vorrätig) */
  bestand: number | null;
  /** Bewertung des Händlers (Buy Box) */
  bewertung: number | null;
  /** größter Rabatt (Streichpreis) einer Variante in % */
  rabattProzent: number | null;
  /** volle Tage seit dem Anlegen */
  alterTage: number;
  /** Produkt steht über ein Buy-Box-Modell (Sammelprodukt) im Shop */
  buyboxModell: boolean;
  /** eigenes Angebot hält die Buy Box (ohne Modell: immer) */
  buyboxGewinner: boolean;
  /** irgendein Angebot des Modells hält die Buy Box */
  buyboxHatGewinner: boolean;
  /** günstigstes Angebot im Modell in € */
  buyboxMinPreis: number | null;
  bestseller: boolean;
  lagerdreher: boolean;
}

/** Text für den Vergleich: Ränder (Leerzeichen, Tab, Zeilenumbruch) weg, klein – in SQL lower(btrim(x, E' \t\n\r')) */
export const regelText = (s: string) => s.replace(/^[ \t\n\r]+|[ \t\n\r]+$/g, '').toLowerCase();
/** Kategorie: zusätzlich Trenner (>, /, ») einheitlich als „ > “ */
export const regelKategorie = (s: string) => regelText(s).replace(/[ \t]*(>|\/|»)[ \t]*/g, ' > ');

const werteVon = (w: string | string[]) => (Array.isArray(w) ? w : [w]);

function textPasst(b: Extract<Bedingung, { feld: RegelTextFeld }>, f: ProduktFakten): boolean {
  const kat = b.feld === 'kategorie';
  const norm = kat ? regelKategorie : regelText;
  const wert = norm(b.feld === 'marke' ? f.marke : b.feld === 'titel' ? f.titel : b.feld === 'kategorie' ? f.kategorie : (f.felder[b.schluessel ?? ''] ?? ''));
  const werte = werteVon(b.wert).map(norm);
  const gleich = (v: string) => wert === v || (kat && wert.startsWith(`${v} > `));
  if (b.op === 'enthaelt') return werte.some((v) => wert.includes(v));
  if (b.op === 'ist_nicht') return !werte.some(gleich);
  return werte.some(gleich);
}

function zahlVon(feld: RegelZahlFeld, f: ProduktFakten): number | null {
  if (feld === 'preis') return f.preis;
  if (feld === 'bestand') return f.bestand;
  if (feld === 'bewertung') return f.bewertung;
  if (feld === 'rabatt_prozent') return f.rabattProzent;
  return f.alterTage;
}

export function bedingungPasst(b: Bedingung, f: ProduktFakten): boolean {
  if (b.feld === 'buybox_gewinner') return f.buyboxGewinner === b.wert;
  if (b.feld === 'bestseller') return f.bestseller === b.wert;
  if (b.feld === 'lagerdreher') return f.lagerdreher === b.wert;
  if ((REGEL_TEXT_FELDER as readonly string[]).includes(b.feld)) return textPasst(b as Extract<Bedingung, { feld: RegelTextFeld }>, f);
  const z = b as Extract<Bedingung, { feld: RegelZahlFeld }>;
  const v = zahlVon(z.feld, f);
  if (v === null) return false;
  if (z.op === 'zwischen') {
    const [a, c] = z.wert as [number, number];
    return v >= Math.min(a, c) && v <= Math.max(a, c);
  }
  const w = z.wert as number;
  return z.op === '>=' ? v >= w : z.op === '<=' ? v <= w : v === w;
}

/** Regel im Speicher: alle aus „und“ und – falls vorhanden – mindestens eine aus „oder“. Leere Regel = alles. */
export function regelPasst(r: Regel, f: ProduktFakten): boolean {
  const teil = (t: Bedingung | Regel) => (istBedingung(t) ? bedingungPasst(t, f) : regelPasst(t, f));
  if (r.und?.length && !r.und.every(teil)) return false;
  if (r.oder?.length && !r.oder.some(teil)) return false;
  return true;
}

/** Regel als Satz für Liste und Detail */
export function regelBeschreibung(r: Regel | null): string {
  if (!r) return '';
  const b = (x: Bedingung): string => {
    const f = REGEL_FELDER[x.feld];
    const feld = x.feld === 'merkmal' ? `${f.name} „${x.schluessel}“` : f.name;
    if (f.art === 'ja') return x.wert ? f.name : `nicht ${f.name}`;
    const w = Array.isArray(x.wert) ? (x.op === 'zwischen' ? x.wert.join(' und ') : x.wert.join(', ')) : String(x.wert);
    return `${feld} ${REGEL_OP_NAMEN[x.op]} ${w}${f.einheit ? ` ${f.einheit}` : ''}`;
  };
  const teil = (t: Bedingung | Regel): string => (istBedingung(t) ? b(t) : `(${regelBeschreibung(t)})`);
  const und = (r.und ?? []).map(teil).join(' und ');
  const oder = (r.oder ?? []).map(teil).join(' oder ');
  return [und, oder ? (und && (r.oder?.length ?? 0) > 1 ? `(${oder})` : oder) : ''].filter(Boolean).join(' und ');
}

/* ------------------------------------------------------------------ Bewerbbarkeit (2.5) */

export const NICHT_BEWERBBAR = {
  inaktiv: 'Nicht aktiv im Shop',
  ausverkauft: 'Ausverkauft – pausiert, bis wieder Bestand da ist',
  kategorie_gesperrt: 'Kategorie für Werbung gesperrt',
  buybox_guenstiger: 'Buy Box verloren an günstigeres Angebot',
  buybox_verloren: 'Buy Box hält ein anderes Angebot',
  keine_buybox: 'Kein Angebot hält die Buy Box',
  preis_zu_hoch: 'Preis zu hoch',
} as const;
export type NichtBewerbbarGrund = keyof typeof NICHT_BEWERBBAR;

export interface BewerbbarkeitRegeln {
  /** Preis höchstens so viel % über dem günstigsten Angebot – null: keine Grenze */
  preisgrenzeProzent: number | null;
  /** normalisiert wie regelKategorie – gilt mit Unterkategorien */
  gesperrteKategorien: string[];
}

/** Standard: keine Preisgrenze (bestehende Kampagnen laufen unverändert), keine gesperrten Kategorien – Vorschlag im Konto 20 % */
export const BEWERBBARKEIT_STANDARD: BewerbbarkeitRegeln = { preisgrenzeProzent: null, gesperrteKategorien: [] };
export const PREISGRENZE_VORSCHLAG = 20;

const AUSSPIELBAR_STATUS = ['freigegeben', 'gelistet'];

/**
 * Warum ein Produkt nicht ausgespielt wird – null: bewerbbar. Reihenfolge = was zuerst behoben werden muss.
 * Händler: das eigene Angebot muss die Buy Box halten; Marke: irgendein Angebot des Sammelprodukts.
 */
export function bewerbbarkeitGrund(f: ProduktFakten, r: BewerbbarkeitRegeln, kontoTyp: 'haendler' | 'marke'): NichtBewerbbarGrund | null {
  if (!AUSSPIELBAR_STATUS.includes(f.status)) return 'inaktiv';
  if (f.bestand !== null && f.bestand <= 0) return 'ausverkauft';
  const kat = regelKategorie(f.kategorie);
  if (r.gesperrteKategorien.some((g) => kat === g || kat.startsWith(`${g} > `))) return 'kategorie_gesperrt';
  if (f.buyboxModell) {
    if (kontoTyp === 'haendler' && !f.buyboxGewinner) {
      return f.buyboxMinPreis !== null && f.preis !== null && f.buyboxMinPreis < f.preis ? 'buybox_guenstiger' : 'buybox_verloren';
    }
    if (kontoTyp === 'marke' && !f.buyboxHatGewinner) return 'keine_buybox';
  }
  if (r.preisgrenzeProzent !== null && f.preis !== null && f.buyboxMinPreis !== null && f.preis > f.buyboxMinPreis * (1 + r.preisgrenzeProzent / 100) + 1e-9) {
    return 'preis_zu_hoch';
  }
  return null;
}

/* ------------------------------------------------------------------ GTIN */

/**
 * GTIN-8/12/13/14 einheitlich als 14 Ziffern: nur Ziffern, Leer- und Bindestriche erlaubt, mindestens 8 Ziffern, führende Nullen
 * vereinheitlicht. In SQL gtin_norm(text) (Migration 0800) – dieselbe Regel, damit Listen den Index treffen.
 */
export function gtin14(roh: string): string | null {
  if (!/^[0-9 -]+$/.test(roh)) return null;
  const d = roh.replace(/[ -]/g, '');
  const z = d.replace(/^0+/, '');
  return d.length >= 8 && z.length >= 1 && z.length <= 14 ? z.padStart(14, '0') : null;
}

/** Prüfziffer (Modulo 10, Gewichte 3/1 von rechts) – auch für die aufgefüllte 14-stellige Form gültig */
export function gtinPruefzifferOk(gtin: string): boolean {
  if (!/^\d{8,14}$/.test(gtin)) return false;
  let summe = 0;
  for (let i = gtin.length - 2, g = 3; i >= 0; i--, g = g === 3 ? 1 : 3) summe += Number(gtin[i]) * g;
  return (10 - (summe % 10)) % 10 === Number(gtin.at(-1));
}

export type GtinErgebnis = { gtin: string } | { fehler: 'format' | 'pruefziffer' };

export function gtinPruefen(roh: string): GtinErgebnis {
  const g = gtin14(roh.trim());
  if (!g) return { fehler: 'format' };
  return gtinPruefzifferOk(g) ? { gtin: g } : { fehler: 'pruefziffer' };
}

/** GS1-Firmenpräfix (6–12 Ziffern, Form der GTIN-13) als Anfang der 14-stelligen Form */
export function gs1Praefix14(roh: string): string | null {
  const d = roh.replace(/[\s-]/g, '');
  return /^\d{6,12}$/.test(d) ? `0${d}` : null;
}

/* ------------------------------------------------------------------ Marken-Unschärfe */

/** Wie produkt.marke verglichen wird (werbekonto_marke) */
export const markeNormalisieren = (s: string) => s.trim().toLowerCase();

/** Schlüssel ohne Umlaute, Akzente, Satz- und Leerzeichen – „Adidas Originals“ → „adidasoriginals“ */
export function markenSchluessel(s: string): string {
  return s.toLowerCase().replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/&/g, 'und').replace(/[^a-z0-9]+/g, '');
}

const woerter = (s: string) => s.toLowerCase().normalize('NFKD').replace(/[̀-ͯ]/g, '').split(/[^a-z0-9äöüß]+/).filter(Boolean);

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const v = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    let vorher = v[0]!;
    v[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const t = v[j]!;
      v[j] = Math.min(v[j]! + 1, v[j - 1]! + 1, vorher + (a[i - 1] === b[j - 1] ? 0 : 1));
      vorher = t;
    }
  }
  return v[b.length]!;
}

/**
 * Ähnlichkeit zweier Markenwerte 0–1: gleicher Schlüssel 1 („adidas“/„ADIDAS“, „L'Oréal“/„Loreal“), erstes Wort gleich
 * und eines enthält das andere 0,9 („adidas“/„adidas Originals“), sonst Editierabstand auf dem Schlüssel.
 */
export function markenAehnlichkeit(a: string, b: string): number {
  const ka = markenSchluessel(a);
  const kb = markenSchluessel(b);
  if (!ka || !kb) return 0;
  if (ka === kb) return 1;
  const wa = woerter(a);
  const wb = woerter(b);
  if (wa[0] && wa[0] === wb[0] && (wa.every((w) => wb.includes(w)) || wb.every((w) => wa.includes(w)))) return 0.9;
  if (Math.min(ka.length, kb.length) >= 4 && (ka.startsWith(kb) || kb.startsWith(ka))) return 0.85;
  const l = Math.max(ka.length, kb.length);
  return Math.round((1 - levenshtein(ka, kb) / l) * 1000) / 1000;
}

/** Ab dieser Ähnlichkeit schlägt das System einen Katalogwert zur Zuordnung vor */
export const MARKEN_SCHWELLE = 0.8;

/** Katalogwerte, die einem zugeordneten Wert ähneln, aber selbst nicht zugeordnet sind – beste Übereinstimmung zuerst */
export function markenVorschlaege(
  zugeordnet: readonly string[],
  katalog: readonly { marke: string; anzahl: number }[],
  ausnehmen: readonly string[] = [],
  schwelle = MARKEN_SCHWELLE,
): { marke: string; anzahl: number; aehnlichZu: string; aehnlichkeit: number }[] {
  const z = new Set(zugeordnet.map(markeNormalisieren));
  const aus = new Set(ausnehmen.map(markeNormalisieren));
  const ergebnis: { marke: string; anzahl: number; aehnlichZu: string; aehnlichkeit: number }[] = [];
  for (const k of katalog) {
    const n = markeNormalisieren(k.marke);
    if (!n || z.has(n) || aus.has(n)) continue;
    let best = { zu: '', wert: 0 };
    for (const m of z) {
      const a = markenAehnlichkeit(m, n);
      if (a > best.wert) best = { zu: m, wert: a };
    }
    if (best.wert >= schwelle) ergebnis.push({ marke: n, anzahl: k.anzahl, aehnlichZu: best.zu, aehnlichkeit: best.wert });
  }
  return ergebnis.sort((a, b) => b.aehnlichkeit - a.aehnlichkeit || b.anzahl - a.anzahl || a.marke.localeCompare(b.marke));
}

/* ------------------------------------------------------------------ EAN-/SKU-Liste */

/** Spaltennamen, die eine Liste als GTIN bzw. SKU ausweisen (Kopfzeile ohne Groß-/Kleinschreibung, Satzzeichen) */
const GTIN_SPALTEN = ['gtin', 'ean', 'ean13', 'gtin13', 'gtin14', 'upc', 'barcode', 'ean-code', 'eancode', 'gtin-code'];
const SKU_SPALTEN = ['sku', 'artikelnummer', 'artikelnr', 'art-nr', 'artnr', 'artikel-nr', 'mpn', 'item number', 'herstellernummer', 'produktnummer'];

/**
 * Spalte einer Tabelle erkennen: zuerst über den Kopf (GTIN/EAN vor SKU/Artikelnummer), sonst die Spalte mit den meisten gültigen
 * GTIN in den ersten Zeilen, sonst die erste. Liefert Index, Name und ob die erste Zeile ein Kopf ist.
 */
export function listenSpalteErkennen(zeilen: readonly string[][]): { index: number; name: string; art: 'gtin' | 'sku' | 'gemischt'; kopf: boolean } {
  const kopf = (zeilen[0] ?? []).map((k) => k.trim().toLowerCase().replace(/[_.:]/g, '').replace(/\s+/g, ' '));
  const g = kopf.findIndex((k) => GTIN_SPALTEN.includes(k) || GTIN_SPALTEN.includes(k.replace(/[\s-]/g, '')));
  if (g >= 0) return { index: g, name: zeilen[0]![g]!.trim(), art: 'gtin', kopf: true };
  const s = kopf.findIndex((k) => SKU_SPALTEN.includes(k) || SKU_SPALTEN.includes(k.replace(/[\s-]/g, '')));
  if (s >= 0) return { index: s, name: zeilen[0]![s]!.trim(), art: 'sku', kopf: true };
  const probe = zeilen.slice(0, 200);
  const breite = Math.max(0, ...probe.map((z) => z.length));
  let best = { index: 0, n: -1 };
  for (let i = 0; i < breite; i++) {
    const n = probe.filter((z) => z[i] && 'gtin' in gtinPruefen(z[i]!)).length;
    if (n > best.n) best = { index: i, n };
  }
  const ersteIstKopf = !!zeilen[0]?.[best.index] && !/\d/.test(zeilen[0]![best.index]!);
  return { index: best.index, name: `Spalte ${best.index + 1}`, art: best.n > probe.length / 2 ? 'gtin' : 'gemischt', kopf: ersteIstKopf };
}

/** Eingefügter Text: ein Wert je Zeile, Trennzeichen egal (Komma, Semikolon, Tab, Leerzeichen zwischen Werten) */
export function listenWerteAusText(text: string): string[] {
  return text.split(/[\r\n,;\t|]+/).flatMap((z) => {
    const t = z.trim();
    if (!t) return [];
    // „4006381 333931“ ist eine GTIN mit Leerzeichen, „ABC-1 ABC-2“ zwei SKU
    return gtin14(t) ? [t] : t.split(/\s+/);
  }).filter(Boolean);
}

export const LISTEN_ERGEBNISSE = {
  gefunden: 'Gefunden',
  nicht_gefunden: 'Nicht gefunden',
  doppelt: 'Doppelt',
  ungueltig: 'Ungültig',
  nicht_bewerbbar: 'Gefunden, nicht bewerbbar',
} as const;
export type ListenErgebnis = keyof typeof LISTEN_ERGEBNISSE;

/* ------------------------------------------------------------------ Eingaben */

const Name = z.string().trim().min(2, 'Bitte Namen angeben').max(120, 'Höchstens 120 Zeichen');
const Ids = (max: number) => z.array(z.string().uuid()).max(max);

export const ProduktgruppeAnlegenSchema = z.object({
  name: Name,
  art: z.enum(PRODUKTGRUPPE_ART_LISTE),
  regel: RegelSchema.nullable().default(null),
  ausschlussRegel: RegelSchema.nullable().default(null),
  /** Auswahl: Produkte gleich mit anlegen */
  produkte: Ids(PRODUKTGRUPPE_GRENZEN.eintraegeJeAufruf).default([]),
}).superRefine((g, ctx) => {
  if (g.art === 'regel' && !g.regel) ctx.addIssue({ code: 'custom', path: ['regel'], message: 'Bitte mindestens eine Bedingung angeben' });
  if (g.art !== 'regel' && g.regel) ctx.addIssue({ code: 'custom', path: ['regel'], message: 'Nur Regel-Gruppen haben eine Regel' });
  if (g.produkte.length && g.art !== 'statisch') ctx.addIssue({ code: 'custom', path: ['produkte'], message: 'Produkte nur bei einer Auswahl' });
});
export type ProduktgruppeAnlegen = z.infer<typeof ProduktgruppeAnlegenSchema>;

export const ProduktgruppeAendernSchema = z.object({
  name: Name,
  regel: RegelSchema.nullable().default(null),
  ausschlussRegel: RegelSchema.nullable().default(null),
});
export type ProduktgruppeAendern = z.infer<typeof ProduktgruppeAendernSchema>;

export const ProduktgruppeEintraegeSchema = z.object({
  produkte: Ids(PRODUKTGRUPPE_GRENZEN.eintraegeJeAufruf).min(1, 'Bitte Produkte auswählen'),
  /** true: Produkte ausnehmen (in jeder Art) statt hinzufügen */
  ausgeschlossen: z.boolean().default(false),
});
export const ProduktgruppeEintraegeEntfernenSchema = z.object({ produkte: Ids(PRODUKTGRUPPE_GRENZEN.eintraegeJeAufruf).min(1) });

export const RegelVorschauSchema = z.object({
  art: z.enum(['alle', 'regel']).default('regel'),
  regel: RegelSchema.nullable().default(null),
  ausschlussRegel: RegelSchema.nullable().default(null),
});
export type RegelVorschauEingabe = z.infer<typeof RegelVorschauSchema>;

export const ListeTextSchema = z.object({ text: z.string().min(1, 'Bitte Werte einfügen').max(2_000_000) });

export const ProduktgruppenAbfrage = z.object({
  cursor: z.string().max(400).optional(),
  archiviert: z.enum(['ja', 'nein']).default('nein'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const EintraegeAbfrage = z.object({
  cursor: z.string().max(200).optional(),
  filter: z.enum(['alle', 'bewerbbar', 'nicht_bewerbbar', 'ausgeschlossen']).default('alle'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export const AnzeigengruppeProduktgruppenSchema = z.object({
  produktgruppen: Ids(PRODUKTGRUPPE_GRENZEN.gruppenJeAnzeigengruppe),
});

/** Schnellkampagne „Bewerben“ (2.6): Produkte, Tagesbudget, Starten */
export const SchnellkampagneSchema = z.object({
  produkte: Ids(PRODUKTGRUPPE_GRENZEN.schnellProdukte).min(1, 'Bitte Produkte auswählen'),
  tagesCent: z.number().int().min(100, 'Mindestens 1 €').max(10_000_000, 'Tagesbudget zu hoch'),
  /** ohne Angabe: Vorschlag aus dem Kategorie-Durchschnitt */
  gebotCent: z.number().int().min(1).max(10_000).nullable().default(null),
  name: z.string().trim().max(120).default(''),
});
export type SchnellkampagneEingabe = z.infer<typeof SchnellkampagneSchema>;

export const BewerbbarkeitSchema = z.object({
  preisgrenzeProzent: z.number().min(0, 'Nicht negativ').max(500, 'Höchstens 500 %').nullable(),
  gesperrteKategorien: z.array(z.string().trim().min(1).max(200)).max(200).transform((l) => [...new Set(l.map(regelKategorie))]),
});
export type BewerbbarkeitEingabe = z.infer<typeof BewerbbarkeitSchema>;

export const MarkenZuordnungSchema = z.object({
  marken: z.array(z.string().trim().min(1).max(120)).max(50),
  gs1Praefixe: z.array(z.string().trim().regex(/^[\d\s-]{6,16}$/, 'GS1-Präfix: 6–12 Ziffern')).max(20).default([])
    .refine((l) => l.every((p) => gs1Praefix14(p) !== null), 'GS1-Präfix: 6–12 Ziffern'),
});
export type MarkenZuordnungEingabe = z.infer<typeof MarkenZuordnungSchema>;

export const MarkenVorschlagSchema = z.object({ werbekontoId: z.string().uuid(), marke: z.string().trim().min(1).max(120), annehmen: z.boolean() });

/* ------------------------------------------------------------------ Antworten */

export interface ProduktgruppeZeile {
  id: string;
  name: string;
  art: ProduktgruppeArt;
  /** Regel als Satz (nur regel) */
  regelText: string;
  anzahlGesamt: number;
  anzahlBewerbbar: number;
  /** in so vielen nicht beendeten Kampagnen verwendet */
  kampagnen: number;
  /** aus einer Schnellkampagne */
  schnell: boolean;
  archiviert: boolean;
  ausgewertetAm: string | null;
  aktualisiertAm: string;
}

export interface ProduktgruppeSeite {
  eintraege: ProduktgruppeZeile[];
  weiter: string | null;
}

export interface ProduktgruppeEintrag {
  produktId: string;
  titel: string;
  bild: string | null;
  marke: string;
  sku: string;
  gtin: string;
  herkunft: 'manuell' | 'liste' | 'regel';
  ausgeschlossen: boolean;
  /** null: bewerbbar */
  grund: NichtBewerbbarGrund | null;
}

export interface ProduktgruppeEintragSeite {
  eintraege: ProduktgruppeEintrag[];
  weiter: string | null;
}

export interface ListenBeispiel {
  zeile: number;
  wert: string;
  ergebnis: ListenErgebnis;
  grund: string;
}

export interface ListenImport {
  id: string;
  status: 'wartet' | 'laeuft' | 'bereit' | 'uebernommen' | 'fehler';
  quelle: 'text' | 'csv' | 'xlsx' | 'api';
  dateiname: string;
  spalte: string;
  zeilen: number;
  verarbeitet: number;
  gefunden: number;
  nichtGefunden: number;
  doppelt: number;
  ungueltig: number;
  nichtBewerbbar: number;
  /** übernommene Produkte (nach „Nur gefundene übernehmen“) */
  uebernommen: number | null;
  /** erste Problemzeilen; alle stehen in der Fehlerdatei */
  beispiele: ListenBeispiel[];
  fehler: string;
  erstelltAm: string;
  fertigAm: string | null;
}

export interface ProduktgruppeDetail extends ProduktgruppeZeile {
  regel: Regel | null;
  ausschlussRegel: Regel | null;
  /** Gründe der nicht bewerbbaren Produkte mit Anzahl */
  gruende: { grund: NichtBewerbbarGrund; anzahl: number }[];
  importe: ListenImport[];
  kampagnenListe: { id: string; name: string; status: string }[];
}

export interface RegelVorschau {
  anzahl: number;
  treffer: { id: string; titel: string; bild: string | null; marke: string; kategorie: string; preisCent: number | null; grund: NichtBewerbbarGrund | null }[];
}

export interface RegelWerte {
  feld: 'marke' | 'kategorie' | 'merkmal';
  werte: string[];
}

export interface SchnellVorschlag {
  /** Tagesbudget aus dem Durchschnitt der laufenden Kampagnen derselben Kategorie, sonst Standard */
  tagesCent: number;
  gebotCent: number;
  /** woraus der Vorschlag stammt */
  quelle: 'kategorie' | 'marktplatz' | 'standard';
  kategorie: string;
  produkte: { id: string; titel: string; bild: string | null; grund: NichtBewerbbarGrund | null }[];
}

export interface SchnellkampagneErgebnis {
  kampagneId: string;
  produktgruppeId: string;
  produkte: number;
  bewerbbar: number;
}

export interface BewerbbarkeitEinstellung extends BewerbbarkeitRegeln {
  /** Kategorien des Katalogs zur Auswahl */
  kategorien: string[];
}

export interface MarkenZuordnungZeile {
  werbekontoId: string;
  firma: string;
  status: string;
  marken: string[];
  gs1Praefixe: string[];
  freigegebenVon: string | null;
  freigegebenAm: string | null;
  /** Produkte im Marktplatz, die der Marke zugeordnet sind */
  produkte: number;
}

export interface MarkenVorschlag {
  werbekontoId: string;
  firma: string;
  marke: string;
  aehnlichZu: string;
  aehnlichkeit: number;
  anzahl: number;
}
